import { flatMultiplier, outcomeMultiplier } from '../config.js';
import { goalBand, marginBucket, RESULT_OPTIONS, winToNil, YES_NO } from './helpers.js';
import { CONFIG } from '../config.js';
const flat = flatMultiplier;
const opt = outcomeMultiplier;
/** The 1X2 anchor plus legs graded from the final score (always gradeable). */
export const SCORE_MARKETS = [
    // ── The 1X2 anchor (the qualifier — not a leg, no multiplier) ────────────────
    {
        id: 'outcome',
        label: 'Who wins?',
        shortLabel: 'Result',
        kind: 'ternary',
        requires: 'score',
        options: RESULT_OPTIONS,
        teamRelative: true,
        anchor: true,
        gradeable: () => true,
        isHit: (s, r) => s === r.outcome,
        winningLabel: (r) => (r.outcome === 'DRAW' ? 'Draw' : r.outcome === 'HOME' ? 'Home win' : 'Away win')
    },
    // ── Legs from the FINAL score (always gradeable) ─────────────────────────────
    {
        id: 'btts',
        label: 'Both teams to score?',
        shortLabel: 'BTTS',
        kind: 'binary',
        multiplier: flat('btts'),
        requires: 'score',
        options: YES_NO,
        gradeable: () => true,
        isHit: (s, r) => s === r.btts,
        winningLabel: (r) => (r.btts === 'YES' ? 'Yes' : 'No')
    },
    {
        id: 'totalGoals',
        label: `Goals ${CONFIG.TOTAL_GOALS_LINE}`,
        shortLabel: `O/U ${CONFIG.TOTAL_GOALS_LINE}`,
        kind: 'binary',
        multiplier: flat('totalGoals'),
        requires: 'score',
        options: [
            { value: 'OVER', label: 'Over' },
            { value: 'UNDER', label: 'Under' }
        ],
        gradeable: () => true,
        isHit: (s, r) => s === r.totalGoals,
        winningLabel: (r) => `${r.totalGoals === 'OVER' ? 'Over' : 'Under'} ${CONFIG.TOTAL_GOALS_LINE}`
    },
    {
        id: 'cleanSheet',
        label: 'Keep a clean sheet?',
        shortLabel: 'Clean sheet',
        kind: 'binary',
        requires: 'score',
        // Team-specific: back ONE side to concede nothing. HOME wins when the AWAY side
        // is shut out (awayGoals === 0) and vice-versa. Priced PER side (an away clean
        // sheet is rarer, so it pays more). This is intentionally NOT the symmetric
        // "any clean sheet" — that was identical to `btts: NO` and let a card silently
        // double-stack the same coin-flip; a team clean sheet is its own characterful bet.
        options: [
            { value: 'HOME', label: 'Home', multiplier: opt('cleanSheet', 'HOME') },
            { value: 'AWAY', label: 'Away', multiplier: opt('cleanSheet', 'AWAY') }
        ],
        teamRelative: true,
        gradeable: () => true,
        isHit: (s, r) => (s === 'HOME' ? r.awayGoals === 0 : r.homeGoals === 0),
        winningLabel: (r) => {
            const homeCs = r.awayGoals === 0;
            const awayCs = r.homeGoals === 0;
            if (homeCs && awayCs)
                return 'Both';
            if (homeCs)
                return 'Home';
            if (awayCs)
                return 'Away';
            return 'Neither';
        }
    },
    {
        id: 'winToNil',
        label: 'A team to win to nil?',
        shortLabel: 'Win to nil',
        kind: 'binary',
        requires: 'score',
        options: [
            { value: 'YES', label: 'Yes', multiplier: opt('winToNil', 'YES') },
            { value: 'NO', label: 'No', multiplier: opt('winToNil', 'NO') }
        ],
        // YES when either side wins without conceding (a draw is always NO).
        gradeable: () => true,
        isHit: (s, r) => s === (winToNil(r) ? 'YES' : 'NO'),
        winningLabel: (r) => (winToNil(r) ? 'Yes' : 'No')
    },
    {
        id: 'winningMargin',
        label: 'Winning margin',
        shortLabel: 'Margin',
        kind: 'ternary',
        requires: 'score',
        options: [
            { value: 'DRAW', label: 'Draw', multiplier: opt('winningMargin', 'DRAW') },
            { value: 'ONE', label: 'By 1', multiplier: opt('winningMargin', 'ONE') },
            { value: 'TWO_PLUS', label: 'By 2+', multiplier: opt('winningMargin', 'TWO_PLUS') }
        ],
        gradeable: () => true,
        isHit: (s, r) => s === marginBucket(r),
        winningLabel: (r) => ({ DRAW: 'Draw', ONE: 'By 1 goal', TWO_PLUS: 'By 2+ goals' })[marginBucket(r)]
    },
    {
        id: 'totalGoalsBand',
        label: 'How many goals?',
        shortLabel: 'Goal band',
        kind: 'band',
        requires: 'score',
        options: [
            { value: 'B0_1', label: '0–1', multiplier: opt('totalGoalsBand', 'B0_1') },
            { value: 'B2_3', label: '2–3', multiplier: opt('totalGoalsBand', 'B2_3') },
            { value: 'B4', label: '4+', multiplier: opt('totalGoalsBand', 'B4') }
        ],
        gradeable: () => true,
        isHit: (s, r) => s === goalBand(r),
        winningLabel: (r) => ({ B0_1: '0–1 goals', B2_3: '2–3 goals', B4: '4+ goals' })[goalBand(r)]
    },
    {
        id: 'exactScore',
        label: 'Exact score',
        shortLabel: 'Exact',
        kind: 'exact',
        multiplier: flat('exactScore'),
        requires: 'score',
        gradeable: () => true,
        isHit: (s, r) => s === r.exactScore,
        winningLabel: (r) => r.exactScore
    }
];
//# sourceMappingURL=catalogScore.js.map